import { useCallback, useState } from 'react';
import { ArrowLeft, History, Plus } from 'lucide-react';
import { Button } from '@staff-app/ui';
import { useTroubleshootSessions, type TroubleshootSession } from '../hooks/useTroubleshoot';
import { useLiveSessionStore } from '../hooks/live-session-store';
import type { DeviceSessionOutput } from '../hooks/useDeviceSessionStream';
import { SessionCard } from './SessionCard';
import { TroubleshootTranscript } from './TroubleshootTranscript';
import { LiveTroubleshootSession } from './LiveTroubleshootSession';
import { DeviceSessionList } from './DeviceSessionList';
import {
  StartLiveSessionDialog,
  liveStartBlockedReason,
  type LiveStartContext,
} from './StartLiveSessionCard';

export type { LiveStartContext } from './StartLiveSessionCard';

/**
 * The device-drawer "Troubleshoot" tab — THIS device's troubleshoot sessions (scoped by UUID).
 * Panel-internal navigation (like scio3's OptiBot panel): a card opens the transcript IN PLACE with a Back
 * that returns to this device's list — it never routes away to the cross-tenant list.
 *
 * `live` opts the host into LIVE troubleshooting (My Testing Devices only): scio3's session-list card
 * with "+ Start session" pinned at the bottom (the form opens in a dialog), and the started session
 * streams inline in this tab. Hosts that don't pass it (the
 * customer Devices drawer) stay history-only. There is no resume: once the live session ends or its token
 * expires, the view hands off to the recorded transcript and the list is refetched.
 */
export function DeviceTroubleshootTab({
  deviceUuid,
  live,
}: {
  deviceUuid: string;
  live?: LiveStartContext;
}) {
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const { data, loading, error, refetch } = useTroubleshootSessions(deviceUuid);
  const sessions = data?.staffTroubleshootSessions ?? [];

  const liveHandle = useLiveSessionStore((s) => (live ? s.byDevice[deviceUuid] : undefined));
  const setLive = useLiveSessionStore((s) => s.set);
  const clearLive = useLiveSessionStore((s) => s.clear);

  const onStarted = useCallback(
    (handle: DeviceSessionOutput) => {
      setLive(deviceUuid, handle);
      void refetch();
    },
    [deviceUuid, refetch, setLive],
  );
  const onEnded = useCallback(() => void refetch(), [refetch]);

  if (liveHandle) {
    return (
      <LiveTroubleshootSession
        key={liveHandle.sessionId}
        session={liveHandle}
        onEnded={onEnded}
        onOpenTranscript={() => {
          clearLive(deviceUuid);
          setSelectedSessionId(liveHandle.sessionId);
        }}
        onClose={() => clearLive(deviceUuid)}
      />
    );
  }

  // Focused: the selected session's transcript + a Back to this device's list (no route change).
  if (selectedSessionId) {
    const selected = sessions.find((s) => s.sessionId === selectedSessionId);
    return (
      <div className="flex flex-col gap-3">
        <button
          type="button"
          onClick={() => setSelectedSessionId(null)}
          className="inline-flex w-fit cursor-pointer items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4 shrink-0" />
          {'Back to sessions'}
        </button>
        <TroubleshootTranscript sessionId={selectedSessionId} session={selected} />
      </div>
    );
  }

  const history = (() => {
    if (error) {
      // staff-api folds open-api's whole error array (stack trace included) into the message, so
      // show its first `message` and keep the raw text behind a disclosure for debugging.
      return (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
          {`Couldn't load sessions: ${firstErrorMessage(error.message)}`}
          <details className="mt-2 text-xs opacity-80">
            <summary className="cursor-pointer">{'Details'}</summary>
            <pre className="mt-1 whitespace-pre-wrap break-all">{error.message}</pre>
          </details>
        </div>
      );
    }
    if (loading && !sessions.length) {
      return <div className="text-sm text-muted-foreground">{'Loading sessions…'}</div>;
    }
    if (!sessions.length) {
      return (
        <div className="text-sm text-muted-foreground">
          {'No troubleshooting sessions for this device.'}
        </div>
      );
    }
    return (
      <div className="space-y-2">
        {sessions.map((s) => (
          <SessionCard key={s.sessionId} session={s} onClick={() => setSelectedSessionId(s.sessionId)} />
        ))}
      </div>
    );
  })();

  if (!live) return history;
  return (
    <LiveHostHistory
      live={live}
      sessions={sessions}
      loading={loading}
      error={error ? firstErrorMessage(error.message) : null}
      onSelect={setSelectedSessionId}
      onStarted={onStarted}
    />
  );
}

/**
 * The live-capable host's list (My Testing Devices) — scio3's `DeviceTroubleshootHistoryPanel` list
 * pane: one bordered card, "Troubleshooting sessions" header, the session rows, and "+ Start session"
 * pinned at the bottom. The button opens the start form in a dialog; it is disabled — with the
 * SecureLink reason shown under it — while the device can't take a live session.
 */
function LiveHostHistory({
  live,
  sessions,
  loading,
  error,
  onSelect,
  onStarted,
}: {
  live: LiveStartContext;
  sessions: TroubleshootSession[];
  loading: boolean;
  error: string | null;
  onSelect: (sessionId: string) => void;
  onStarted: (handle: DeviceSessionOutput) => void;
}) {
  const [startOpen, setStartOpen] = useState(false);
  const blocked = liveStartBlockedReason(live);
  return (
    <div
      className="flex h-full min-h-[24rem] w-full flex-col overflow-hidden rounded-lg border border-border bg-card"
      data-testid="troubleshoot-history-card"
    >
      <header className="flex items-center gap-2 border-b border-border px-3 py-2 text-sm font-medium">
        <History className="h-4 w-4 text-muted-foreground" aria-hidden />
        {'Troubleshooting sessions'}
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {error ? (
          <div className="p-4 text-xs text-destructive">{`Couldn't load sessions: ${error}`}</div>
        ) : (
          <DeviceSessionList sessions={sessions} loading={loading} onSelect={onSelect} />
        )}
      </div>
      <footer className="flex flex-col items-center gap-1 border-t border-border p-2">
        <div className="flex w-full max-w-xs justify-center">
          <Button
            size="md"
            className="w-full"
            onClick={() => setStartOpen(true)}
            disabled={!!blocked}
            disabledTooltip={blocked ?? undefined}
            data-testid="troubleshoot-start-cta"
          >
            <Plus className="h-4 w-4" aria-hidden />
            {'Start session'}
          </Button>
        </div>
        {blocked ? (
          <p className="text-center text-[11px] text-muted-foreground" data-testid="troubleshoot-start-cta-blocked">
            {blocked}
          </p>
        ) : null}
      </footer>
      <StartLiveSessionDialog
        open={startOpen}
        onClose={() => setStartOpen(false)}
        ctx={live}
        onStarted={onStarted}
      />
    </div>
  );
}

/** The first `"message":"…"` inside a folded open-api error, else the text itself (clipped). */
export function firstErrorMessage(raw: string): string {
  const m = /"message"\s*:\s*"((?:[^"\\]|\\.)*)"/.exec(raw ?? '');
  const text = m ? m[1] : (raw ?? '');
  return text.length > 200 ? `${text.slice(0, 200)}…` : text;
}
